// axiosInstance.js
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import store from "../store/reducers/store";
import { refreshToken } from "../store/reducers/authReducer";
import { apiEndpoint } from "../urls";
import { logoutUser } from "../Helpers/logoutHelper";
import { logUserEvent } from "./logger";

const axiosInstance = axios.create({
  baseURL: apiEndpoint.developerToolBaseUri,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request Interceptor: Attach token
axiosInstance.interceptors.request.use(
  (config) => {
    if (config.authRequired) {
      const token = Cookies.get("_d_token");
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    }
    const page = window.location.pathname;
    logUserEvent("API Request", `Calling API: ${config.url}`, page, {
      method: config.method,
      data: config.data,
      headers: config.headers,
    });
    return config;
  },
  (error) => {
    const page = window.location.pathname;
    logUserEvent("API Request Error", "Failed to initiate API call", page, {
      error,
    });
    return Promise.reject(error);
  }
);

// Handle 401 auto-refresh logic
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Response Interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    const page = window.location.pathname;
    logUserEvent("API Response", `API Success: ${response.config.url}`, page, {
      status: response.status,
      data: response.data,
    });
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const page = window.location.pathname;
    const status = error.response?.status;
    const message = error.response?.data?.message || "Something went wrong";

    logUserEvent("API Error", `API Error: ${error.config?.url}`, page, {
      status,
      message,
      method: error.config?.method,
      data: error.config?.data,
      response: error.response?.data,
    });

    // Handle 401 Unauthorized
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
              resolve(axiosInstance(originalRequest));
            },
            reject: reject,
          });
        });
      }

      isRefreshing = true;

      try {
        const result = await store.dispatch(refreshToken()).unwrap();
        const newToken = result?.access;

        Cookies.set("_d_token", newToken, { expires: 30 });

        processQueue(null, newToken);
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        toast.error("Session expired. Please login again.");
        logoutUser(); // ⛔ force logout
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other errors
    switch (status) {
      case 400:
        toast.error(`Bad Request: ${message}`);
        break;
      case 403:
        toast.warn("Forbidden: You don’t have access.");
        break;
      case 404:
        toast.info(`Not Found: ${message}`);
        break;
      case 500:
        toast.error("Internal Server Error. Try again later.");
        break;
      default:
        toast.error(message);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
